import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Typography,
  Button,
  Box,
  Chip,
  Divider,
  Stack,
  Avatar,
  Grid,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import ChairIcon from '@mui/icons-material/Chair';
import SecurityIcon from '@mui/icons-material/Security';
import EventIcon from '@mui/icons-material/Event';
import TvIcon from '@mui/icons-material/Tv';
import PaymentIcon from '@mui/icons-material/Payment';

/**
 * Component to display individual venue space details
 */
const SpaceCard = ({ space, onEdit, onDelete, disabled = false }) => {
  const {
    name,
    seatingCapacity,
    standingCapacity,
    pricingOptions = [],
    images = [],
    floorplanImage,
    idealEvents = [],
    techAvailable = [],
    features = [],
    description
  } = space;

  // Format price with currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Get the first image for the card preview
  const previewImage = images && images.length > 0 
    ? (typeof images[0] === 'string' ? images[0] : 
      (images[0] instanceof File ? URL.createObjectURL(images[0]) : 
      (images[0].url || null)))
    : null;

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {previewImage && (
        <CardMedia
          component="img"
          height="140"
          image={previewImage}
          alt={name}
          sx={{ objectFit: 'cover' }}
        />
      )}
      
      <CardContent sx={{ flexGrow: 1, pt: 2 }}>
        <Typography variant="h6" component="h3" gutterBottom noWrap>
          {name}
        </Typography>
        
        <Divider sx={{ my: 1.5 }} />
        
        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          <Grid item xs={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ChairIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Seated: {seatingCapacity}
              </Typography>
            </Stack>
          </Grid>
          
          <Grid item xs={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PeopleIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Standing: {standingCapacity}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
        
        <Box sx={{ mb: 1.5 }}>
          {pricingOptions && pricingOptions.length > 0 ? (
            <>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <PaymentIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  <strong>Pricing Options:</strong>
                </Typography>
              </Stack>
              <Box>
                {pricingOptions.map((option, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      ml: 1, 
                      mb: 1.5, 
                      pb: 1, 
                      borderBottom: index < pricingOptions.length - 1 ? '1px dashed #e0e0e0' : 'none' 
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'medium' }}>
                      {formatCurrency(option.price)} <span style={{ color: 'text.secondary' }}>({option.strategy})</span>
                    </Typography>
                    
                    {option.description && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          mb: 0.5,
                          pl: 1,
                          fontStyle: 'italic',
                          fontSize: '0.85rem'
                        }}
                      >
                        {option.description}
                      </Typography>
                    )}
                    
                    {(option.minHours || option.minGuests || option.securityDeposit) && (
                      <Box sx={{ pl: 1, fontSize: '0.8rem', color: 'text.secondary' }}>
                        {option.minHours && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            Min. {option.minHours} hours required
                          </Typography>
                        )}
                        
                        {option.minGuests && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            Min. {option.minGuests} guests required
                          </Typography>
                        )}
                        
                        {option.securityDeposit && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            Security Deposit: {formatCurrency(option.securityDeposit)}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No pricing information available
            </Typography>
          )}
        </Box>
        
        {description && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 1.5, 
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis' 
            }}
          >
            {description}
          </Typography>
        )}
        
        {idealEvents.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <EventIcon fontSize="small" color="action" />
              <Typography variant="body2">
                <strong>Ideal For:</strong>
              </Typography>
            </Stack>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {idealEvents.map((event) => (
                <Chip 
                  key={event} 
                  label={event} 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                />
              ))}
            </Box>
          </Box>
        )}
        
        {techAvailable.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <TvIcon fontSize="small" color="action" />
              <Typography variant="body2">
                <strong>Tech Available:</strong>
              </Typography>
            </Stack>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {techAvailable.map((tech) => (
                <Chip 
                  key={tech} 
                  label={tech} 
                  size="small" 
                  color="info" 
                  variant="outlined" 
                />
              ))}
            </Box>
          </Box>
        )}
        
        {features.length > 0 && (
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Features:</strong>
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {features.map((feature) => (
                <Chip 
                  key={feature} 
                  label={feature} 
                  size="small" 
                  color="secondary" 
                  variant="outlined" 
                />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
      
      <CardActions sx={{ justifyContent: 'flex-end', p: 1.5, pt: 0 }}>
        <Button
          startIcon={<EditIcon />}
          size="small"
          onClick={onEdit}
          disabled={disabled}
        >
          Edit
        </Button>
        <Button
          startIcon={<DeleteIcon />}
          size="small"
          color="error"
          onClick={onDelete}
          disabled={disabled}
        >
          Delete
        </Button>
      </CardActions>
    </Card>
  );
};

export default SpaceCard; 